// Passo 4 · Riepilogo (tela, artboard 34): il progetto a tutta larghezza, requisiti e team
// affiancati, le fasi con cosa succede, chi le guida e quando. «Cambia» torna al passo.
import type { ReactNode } from 'react';
import { Pop } from '../../../components/Pop';
import type { AgentProfile, GitRegistry, ProjectInput, ProjectPlan } from '../../../lib/api';
import { folderOf, schedule, shortDay } from './plan';

function Block({
  title,
  onEdit,
  children,
}: {
  title: string;
  onEdit?: () => void;
  children: ReactNode;
}) {
  return (
    <section className="np-card">
      <header className="np-card-head">
        <h2 className="t-overline">{title}</h2>
        {onEdit && (
          <button
            type="button"
            className="dash-more"
            style={{ marginLeft: 'auto' }}
            onClick={onEdit}
          >
            Cambia
          </button>
        )}
      </header>
      {children}
    </section>
  );
}

export function StepSummary({
  form,
  plan,
  registries,
  profiles,
  onEdit,
}: {
  form: ProjectInput;
  plan: ProjectPlan;
  registries: GitRegistry[];
  profiles: AgentProfile[];
  onEdit: (step: number) => void;
}) {
  const dates = schedule(plan);
  const registry = registries.find((r) => r.id === form.git?.registry);
  const sections = [...plan.requirements.matchAll(/^##\s+(.+)$/gm)].map((m) => m[1]);
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Block title="Progetto" onEdit={() => onEdit(0)}>
        <div className="t-h2">{form.name}</div>
        {form.description && <div className="t-body dim">{form.description}</div>}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <span className="np-chip">
            {form.git ? `${form.git.repo} · ${registry?.name ?? 'git'}` : 'solo locale, senza git'}
          </span>
          <span className="np-chip">
            works/projects/{form.workspace.folder || folderOf(form.name)}
          </span>
          <span className="np-chip">
            {form.baseBranch} → {form.workBranch}
          </span>
        </div>
      </Block>
      <div className="np-two" style={{ alignItems: 'stretch' }}>
        <Block title="Requisiti" onEdit={() => onEdit(1)}>
          {plan.requirements.trim() ? (
            sections.length ? (
              <ul className="np-list">
                {sections.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            ) : (
              <div className="t-small dim clamp2">{plan.requirements}</div>
            )
          ) : (
            <div className="t-caption faint">Nessun requisito scritto.</div>
          )}
        </Block>
        <Block title={`Team · ${plan.team.length} pops`} onEdit={() => onEdit(2)}>
          {plan.team.map((m) => (
            <div key={m.name} className="np-row">
              {m.avatar && <Pop avatar={m.avatar} size={40} label={m.name} />}
              <span style={{ flex: 1, minWidth: 0 }}>
                <span className="t-strong" style={{ display: 'block' }}>
                  {m.name}
                </span>
                <span className="t-caption dim">
                  {profiles.find((p) => p.id === m.profile)?.label ?? m.profile}
                  {m.model ? ` · ${m.model}` : ''}
                </span>
              </span>
              {m.phase && <span className="dash-pill np-phase-pill">{m.phase}</span>}
            </div>
          ))}
        </Block>
      </div>
      <Block title="Fasi" onEdit={() => onEdit(2)}>
        {plan.phases.map((p, i) => {
          const owner = plan.team.find((m) => m.name === p.owner);
          return (
            <div key={i} className="np-summary-phase">
              <span className="np-num">{i + 1}</span>
              <span style={{ minWidth: 0 }}>
                <span className="t-strong" style={{ display: 'block' }}>
                  {p.name}
                </span>
                <span className="t-small dim">{p.description}</span>
              </span>
              <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {owner?.avatar && <Pop avatar={owner.avatar} size={30} />}
                <span className="t-small">{p.owner || '—'}</span>
              </span>
              <span style={{ textAlign: 'right' }}>
                <span className="t-small" style={{ display: 'block' }}>
                  {dates[i] ? `${shortDay(dates[i].from)} → ${shortDay(dates[i].to)}` : ''}
                </span>
                <span className="t-caption faint">
                  {p.days} {p.days === 1 ? 'giorno' : 'giorni'}
                </span>
              </span>
            </div>
          );
        })}
      </Block>
    </div>
  );
}
